import { AnalysisToolBarContainer } from "@components/analysis-nav-bar/portal";
import { SHStack, SHTypography } from "@components/design-systems";
import AccessDeniedDialog from "@components/dialogs/access-denied";
import UnsavedDialog from "@components/dialogs/unsaved";
import { PageRoutes } from "@constants";
import { useFeatureTemplate } from "@hooks/useFeatureTemplate";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import {
  Step,
  StepperButtonState,
  StepperLayout,
  StepperLayoutRef,
} from "@layouts/stepper";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { AnalysisName } from "@pages/platform-analysis-v2/_id/components/analysis-name";
import { AnalysisToolbarButtons } from "@pages/platform-analysis-v2/_id/components/analysis-toolbar-buttons";
import { getAnalysisInitialSteps } from "@pages/platform-analysis-v2/_id/utils";
import { PlatformAnalysisHiddenFooterStepIndexes } from "@pages/platform-analysis/constant";
import { SaveProgressWarningDialog } from "@pages/platform-analysis/_id/steps/fee/calculator/components/save-progress-warning-dialog";
import { RootState } from "@redux/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useToggle } from "react-use";
import { SaveFeatureSelectionAsTemplateDialog } from "./components/dialogs/save-as-template";

export const AnalysisDetails = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const { isSupplierGroup } = useUserPermissions();

  const {
    ui: {
      isDirty,
      isValid,
      isSubmitting,
      isValidating,
      isAccessDenied,
      isLoadingSteps,
      isLoadingBanner,
      shouldShowSaveProgressWarning,
      isValidPortfolioSetups,
      steps,
      selectedStepIndex,
      selectedSubStepIndex,
      isLoading: isLoadingStepBody,
    },
    analysisSteps,
  } = useSelector((state: RootState) => state.platformAnalysis);
  const {
    featureTemplateUI: { isLoading: isLoadingTemplate },
  } = useSelector((state: RootState) => state.featureTemplate);
  const { loadExistingFeatureTemplate } = useFeatureTemplate();
  const {
    isCompleted,
    resetStore,
    setAnalysisSteps,
    setIsAccessDenied,
    setShowSaveProgressWarning,
    setSelectedStepIndex,
    setSelectedSubStepIndex,
    loadAnalysisBannerName,
    loadPlatformAnalysisStep,
  } = usePlatformAnalysis();

  // Refs
  const stepperRef = useRef<StepperLayoutRef | null>(null);
  const lastedAction = useRef<"save" | "next" | "back" | null>(null);
  const prevAnalysisIdRef = useRef(analysisId);

  // States
  const [currentStep, setCurrentStep] = useState<string | undefined>();
  const [currentSubStep, setCurrentSubStep] = useState<string | undefined>();
  const [buttonState, setButtonState] = useState<StepperButtonState>({
    isCanNext: true,
    isCanPrevious: false,
  });
  const [saveAsTemplateDialogOpening, toggleSaveAsTemplateDialogShow] =
    useToggle(false);

  // Functions
  const handleOnSaveAsTemplate = async () => {
    toggleSaveAsTemplateDialogShow(true);
    await loadExistingFeatureTemplate();
  };

  const handleOnExit = () => {
    stepperRef?.current?.onExit?.();
    navigate(PageRoutes.platformAnalysis);
  };

  const handleOnBack = () => {
    lastedAction.current = "back";
    stepperRef?.current?.movePreviousStep?.();
  };

  const handleOnSave = () => {
    lastedAction.current = "save";
    stepperRef?.current?.onSubmit?.();
  };

  const handleOnNext = () => {
    lastedAction.current = "next";
    stepperRef?.current?.moveNextStep?.();
  };

  const handleOnSubmit = async () => {
    return stepperRef.current?.onSubmit?.();
  };

  const handleOnChangeLatestAction = (
    latestAction: "save" | "next" | "back" | null,
  ) => {
    lastedAction.current = latestAction;
  };

  // Effects and Memos
  useEffect(() => {
    if (
      analysisId &&
      analysisId !== "new" &&
      prevAnalysisIdRef.current !== "new"
    ) {
      loadAnalysisBannerName(analysisId).then(async (response) => {
        !!response && (await loadPlatformAnalysisStep(analysisId));
      });
    } else {
      setAnalysisSteps({
        steps: [],
      });
      setIsAccessDenied(false);
    }
    prevAnalysisIdRef.current = analysisId;
    // eslint-disable-next-line
  }, [analysisId]);

  useEffect(() => {
    return () => {
      resetStore();
    };
    // eslint-disable-next-line
  }, []);

  const initialSteps: Step[] = useMemo(
    () => getAnalysisInitialSteps({ steps, isSupplierGroup }),
    [isSupplierGroup, steps],
  );

  const isLoadingStepLayout = useMemo(
    () => isLoadingBanner || isLoadingSteps || isAccessDenied === undefined,
    [isAccessDenied, isLoadingBanner, isLoadingSteps],
  );

  const isFeeCalculatorStep = useMemo(
    () =>
      currentStep === AnalysisStep.Fee &&
      currentSubStep === AnalysisSubStep.Selection,
    [currentStep, currentSubStep],
  );

  const isFeatureAnalysisStep = useMemo(
    () =>
      currentStep === AnalysisStep.Feature &&
      currentSubStep === AnalysisSubStep.Analysis,
    [currentStep, currentSubStep],
  );

  const isHiddenFooter = useMemo(() => {
    return !!PlatformAnalysisHiddenFooterStepIndexes.find(
      (item) =>
        item.selectedStep === currentStep &&
        (item.selectedSubStepIndex === selectedSubStepIndex || !!isCompleted),
    );
  }, [currentStep, selectedSubStepIndex, isCompleted]);

  if (isAccessDenied) return <AccessDeniedDialog />;

  return (
    <>
      <AnalysisToolBarContainer>
        <SHStack
          spacing={"25px"}
          direction="row"
          justifyContent={"space-between"}
          alignItems={"center"}
          height="100%"
        >
          <AnalysisName />
          <AnalysisToolbarButtons
            currentStep={currentStep}
            currentSubStep={currentSubStep}
            buttonState={buttonState}
            handleOnSaveAsTemplate={handleOnSaveAsTemplate}
            handleOnExit={handleOnExit}
            handleOnBack={handleOnBack}
            handleOnSave={handleOnSave}
            handleOnNext={handleOnNext}
            handleOnSubmit={handleOnSubmit}
            lastedAction={lastedAction.current}
          />
        </SHStack>
      </AnalysisToolBarContainer>

      <StepperLayout
        isUsePortal
        ref={stepperRef}
        steps={initialSteps}
        selectedStepIndex={analysisSteps === undefined ? -1 : selectedStepIndex}
        selectedSubStepIndex={selectedSubStepIndex}
        isLoading={isLoadingStepLayout}
        isCompleted={isCompleted}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        isValidating={isValidating}
        isShowPAFooterAction={!isHiddenFooter}
        isLoadingStepBody={isLoadingStepBody}
        analysisId={analysisId}
        latestAction={lastedAction}
        onChangeSelectedStepIndex={setSelectedStepIndex}
        onChangeSelectedSubStepIndex={setSelectedSubStepIndex}
        onChangeButtonState={setButtonState}
        onChangeCurrentStep={setCurrentStep}
        onChangeCurrentSubStep={setCurrentSubStep}
        onSaveAsTemplate={handleOnSaveAsTemplate}
        onChangeLatestAction={handleOnChangeLatestAction}
      />

      <SaveFeatureSelectionAsTemplateDialog
        open={saveAsTemplateDialogOpening && !isLoadingTemplate}
        isFeatureAnalysisStep={isFeatureAnalysisStep}
        onClose={() => toggleSaveAsTemplateDialogShow(false)}
      />

      {isFeeCalculatorStep && !isValidPortfolioSetups ? (
        <UnsavedDialog
          isDirty={isDirty}
          width={1005}
          header={
            <SHStack alignItems="center" textAlign={"center"}>
              <SHTypography variant="h2" lineHeight={"120%"} width={"70%"}>
                To save progress, each platform must have a valid portfolio
              </SHTypography>
            </SHStack>
          }
          body={
            <SHStack alignItems="center" textAlign={"center"} gap={2}>
              <SHTypography variant="body2">
                Some platforms in your analysis do not have a valid portfolio
                assigned to them. Each platform must have a portfolio.
              </SHTypography>
              <SHTypography variant="body2">
                If you go back or exit, you will lose the data already entered
                into the fee calculator. Would you like to discard them?
              </SHTypography>
            </SHStack>
          }
        />
      ) : (
        <UnsavedDialog isDirty={isDirty} />
      )}

      <SaveProgressWarningDialog
        open={isFeeCalculatorStep && shouldShowSaveProgressWarning}
        onDiscard={() => {
          setSelectedSubStepIndex(
            selectedStepIndex === 1
              ? selectedSubStepIndex
              : (selectedSubStepIndex ?? 0) + 1,
          );
          setSelectedStepIndex(selectedStepIndex && selectedStepIndex - 1);
          setShowSaveProgressWarning(false);
        }}
        onReturn={() => {
          setShowSaveProgressWarning(false);
        }}
      />
    </>
  );
};
